import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSensorEdit,
  updateSensor,
  getSensorCreateOptions,
  createSensor,
  getSensorStreamById,
  searchSensorStreamsPaginated,
} from '../api';
import { sensorsKeys } from './useSensors';
import { subscriptionKeys } from '@/features/subscriptions/hooks/queryKeys';
import type { SensorEditForm, SensorCreateForm } from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const sensorEditKeys = {
  edit: (id: string | null) => ['sensor-edit', id] as const,
  streamOptions: () => ['sensor-edit', 'streams'] as const,
  streamsInfinite: (
    organisationId: string | null | undefined,
    currentSensorId: string | null | undefined,
    search: string,
  ) =>
    [...sensorEditKeys.streamOptions(), 'infinite', organisationId ?? null, currentSensorId ?? null, search] as const,
  stream: (id: string) => [...sensorEditKeys.streamOptions(), 'detail', id] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/** Fetch sensor + dropdown options for editing */
export function useSensorEdit(sensorId: string | null, enabled = true) {
  return useQuery({
    queryKey: sensorEditKeys.edit(sensorId),
    queryFn: () => getSensorEdit(sensorId!),
    enabled: enabled && !!sensorId,
    staleTime: 0,
  });
}

/** Fetch dropdown options for create sensor form */
export function useSensorCreateOptions(enabled = true, organisationId?: string | null) {
  return useQuery({
    queryKey: ['sensor-create-options', organisationId ?? null] as const,
    queryFn: () => getSensorCreateOptions(organisationId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

const STREAM_PAGE_SIZE = 50;

export function useSensorStreamsInfinite(params: {
  search: string;
  organisationId?: string | null;
  currentSensorId?: string | null;
}) {
  return useInfiniteQuery({
    queryKey: sensorEditKeys.streamsInfinite(
      params.organisationId,
      params.currentSensorId,
      params.search,
    ),
    queryFn: ({ pageParam = 0 }) =>
      searchSensorStreamsPaginated({
        search: params.search,
        organisationId: params.organisationId,
        currentSensorId: params.currentSensorId,
        pageNumber: pageParam,
        pageLength: STREAM_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.organisationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSensorStream(streamId: string | undefined) {
  return useQuery({
    queryKey: sensorEditKeys.stream(streamId ?? ''),
    queryFn: () => getSensorStreamById(streamId!),
    enabled: !!streamId && streamId !== 'all',
    staleTime: 5 * 60 * 1000,
  });
}

/** Create a new sensor */
export function useCreateSensor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SensorCreateForm) => createSensor(data),
    onSuccess: () => {
      toast.success('Sensor created');
      queryClient.invalidateQueries({ queryKey: sensorsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['account-sensors'] });
      queryClient.invalidateQueries({ queryKey: sensorEditKeys.streamOptions() });
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error('Failed to create sensor', { description: message });
    },
  });
}

/** Save sensor changes */
export function useUpdateSensor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sensorId,
      changes,
    }: {
      sensorId: string;
      changes: Partial<SensorEditForm>;
    }) => updateSensor(sensorId, changes),
    onSuccess: (_data, variables) => {
      toast.success('Sensor updated');
      queryClient.invalidateQueries({ queryKey: sensorsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['account-sensors'] });
      queryClient.invalidateQueries({ queryKey: sensorEditKeys.edit(variables.sensorId) });
      queryClient.invalidateQueries({ queryKey: sensorEditKeys.streamOptions() });
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error('Failed to update sensor', { description: message });
    },
    onSettled: (_data, _error, variables) => {
      // Calibration PATCH can commit before a later legacy field PUT fails.
      // Read back subscription quantities even when the combined save errors.
      if (variables.changes.calibrationIncluded !== undefined) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      }
    },
  });
}
